import { DAY } from "./time";
import type { CountryCode, DocKind, DocStatus, Employee } from "./types";

const EXPIRY_WARNING_DAYS = 60;

/**
 * The papers an employer must hold.
 *
 * Banking is deliberately not here any more. It is entered rather than
 * uploaded — see core/banking — because nobody can pay into a photograph, and
 * a document that is really four fields was always going to be retyped by
 * somebody, which is where a wrong account number comes from.
 */
export function requiredDocs(employee: Employee, orgCountry: CountryCode): DocKind[] {
  const base: DocKind[] = ["Contract", "ID document"];
  if (employee.nationality !== orgCountry) base.push("Work permit");
  return base;
}

export function docState(employee: Employee, orgCountry: CountryCode, now = Date.now()) {
  return requiredDocs(employee, orgCountry).map((kind) => {
    const doc = employee.docs.find((d) => d.kind === kind) ?? null;
    let status: DocStatus = "ok";
    if (!doc) status = "missing";
    else if (doc.validUntil && doc.validUntil < now) status = "expired";
    else if (doc.validUntil && doc.validUntil < now + EXPIRY_WARNING_DAYS * DAY) status = "expiring";
    return { kind, status, doc };
  });
}

/** An expired permit warns the employer. It never blocks the clock:
 *  the work happened, the register must say so, and an admin lapse is
 *  not the worker's debt to carry. */
export function documentAlerts(employees: Employee[], orgCountry: CountryCode) {
  const rank: Record<DocStatus, number> = { expired: 0, missing: 1, expiring: 2, ok: 3 };
  return employees
    .flatMap((e) => docState(e, orgCountry).filter((s) => s.status !== "ok").map((s) => ({ ...s, employee: e })))
    .sort((a, b) => rank[a.status] - rank[b.status]);
}
